import { useEffect, useRef, useState } from "react";
import { api, errMsg, openPdf, fileUrl } from "../lib/api";
import { cacheSet, cacheGet, queueAdd, queueAll, queueRemove } from "../lib/offlineDb";
import { salePriceFor, stockBreakdown, unitOptionLabel } from "../lib/units";
import { fmtMoney, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Modal, btnPrimary, btnSecondary, inputCls, selectCls, Field } from "../components/Modal";
import { toast } from "sonner";
import { ScanBarcode, Plus, Minus, Trash2, ShoppingCart, Printer, X, WifiOff } from "lucide-react";

export default function POS() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const payMethods = user?.tenant?.payment_methods?.length ? user.tenant.payment_methods : PAYMENT_METHODS;
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [discount, setDiscount] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [payments, setPayments] = useState([{ _k: crypto.randomUUID(), method: "Espèces", amount: "" }]);
  const [lastSale, setLastSale] = useState(null);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const searchRef = useRef(null);

  const offlineFilter = async (q = "") => {
    const cat = (await cacheGet("catalog_full")) || [];
    const needle = q.trim().toLowerCase();
    const items = needle
      ? cat.filter((p) => [p.name, p.sku, p.barcode, p.category].filter(Boolean).join(" ").toLowerCase().includes(needle))
      : cat;
    setProducts(items.slice(0, 48));
  };

  const fetchProducts = (q = "") => {
    if (!navigator.onLine) { offlineFilter(q); return; }
    api.get("/products", { params: { search: q, status: "active", limit: 48 } })
      .then((r) => setProducts(r.data.items))
      .catch(() => offlineFilter(q));
  };

  const load = () => {
    api.get("/products", { params: { status: "active", limit: 1000 } })
      .then((r) => cacheSet("catalog_full", r.data.items)).catch(() => {});
    api.get("/customers")
      .then((r) => { setCustomers(r.data); cacheSet("customers", r.data); })
      .catch(async () => setCustomers((await cacheGet("customers")) || []));
    api.get("/warehouses").then((r) => {
      setWarehouses(r.data);
      cacheSet("warehouses", r.data);
      if (r.data.length && !warehouseId) setWarehouseId(r.data[0].id);
    }).catch(async () => {
      const cached = (await cacheGet("warehouses")) || [];
      setWarehouses(cached);
      if (cached.length && !warehouseId) setWarehouseId(cached[0].id);
    });
  };

  const syncQueue = async () => {
    const items = await queueAll();
    if (!items.length) { setPendingCount(0); return; }
    if (!navigator.onLine) { setPendingCount(items.length); return; }
    let synced = 0;
    for (const it of items) {
      try {
        await api.post("/sales", it.payload);
        await queueRemove(it.ref);
        synced++;
      } catch (e) {
        if (e.response?.status === 401 || e.response?.status === 403) {
          toast.error("Session expirée — reconnectez-vous pour synchroniser les ventes hors-ligne");
          break;
        }
        if (e.response) {
          await queueRemove(it.ref);
          toast.error(`Vente hors-ligne rejetée : ${errMsg(e)}`);
        } else break;
      }
    }
    if (synced) { toast.success(`${synced} vente(s) hors-ligne synchronisée(s)`); load(); fetchProducts(search); }
    const rest = await queueAll();
    setPendingCount(rest.length);
  };

  useEffect(() => {
    const on = () => { setOffline(false); syncQueue(); };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    queueAll().then((q) => setPendingCount(q.length));
    syncQueue();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchProducts(search), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [search]);

  const addToCart = (p, unit = "") => {
    // Unité par défaut du produit si définie (et autorisée à la vente)
    if (!unit) {
      const def = (p.units || []).find((u) => u.is_default && u.sellable !== false);
      if (def) unit = def.unit;
    }
    setCart((c) => {
      const idx = c.findIndex((l) => l.product_id === p.id && l.unit === unit);
      if (idx >= 0) {
        const n = [...c];
        n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
        return n;
      }
      const cust = customers.find((x) => x.id === customerId);
      return [...c, { product_id: p.id, name: p.name, qty: 1, unit, base_unit: p.unit,
        units: (p.units || []).filter((u) => u.sellable !== false),
        unit_price: unit ? salePriceFor(p, unit, cust) : priceFor(p), sale_price: p.sale_price, stock: p.stock, discount: 0 }];
    });
    toast.success(`${p.name} ajouté`, { duration: 1200 });
  };

  const priceFor = (p) => {
    const cust = customers.find((c) => c.id === customerId);
    if (cust && ["grossiste", "revendeur"].includes(cust.price_level) && p.wholesale_price > 0) return p.wholesale_price;
    return p.sale_price;
  };

  useEffect(() => {
    setCart((c) => c.map((l) => {
      const p = products.find((x) => x.id === l.product_id);
      return p ? { ...l, unit_price: priceFor(p), sale_price: p.sale_price } : l;
    }));
  }, [customerId]);

  const onScan = async (e) => {
    if (e.key !== "Enter" || !search.trim()) return;
    const code = search.trim();
    try {
      const { data } = await api.get("/products/scan", { params: { code } });
      addToCart(data);
      setSearch("");
    } catch {
      if (products.length === 1) { addToCart(products[0]); setSearch(""); }
      else toast.error("Aucun produit pour ce code");
    }
  };

  const setQty = (i, qty) => setCart((c) => c.map((l, j) => j === i ? { ...l, qty: Math.max(0.01, qty) } : l));
  const setPrice = (i, price) => setCart((c) => c.map((l, j) => j === i ? { ...l, unit_price: Math.max(0, price) } : l));
  const setUnit = (i, unit) => setCart((c) => c.map((l, j) => {
    if (j !== i) return l;
    const prod = products.find((x) => x.id === l.product_id) || { units: l.units, sale_price: l.sale_price };
    const cust = customers.find((x) => x.id === customerId);
    return { ...l, unit, unit_price: salePriceFor(prod, unit, cust) };
  }));
  const removeLine = (i) => setCart((c) => c.filter((_, j) => j !== i));

  const subtotal = cart.reduce((s, l) => s + l.unit_price * l.qty - l.discount, 0);
  const total = Math.max(0, subtotal - Number(discount || 0));
  const paidSum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const submitSale = async () => {
    setSaving(true);
    const payload = {
      customer_id: customerId, warehouse_id: warehouseId, discount: Number(discount) || 0,
      items: cart.map((l) => ({ product_id: l.product_id, qty: l.qty, unit: l.unit,
        unit_price: hasPerm("sales.edit_price") && l.unit_price !== l.sale_price ? l.unit_price : null, discount: l.discount })),
      payments: payments.filter((p) => Number(p.amount) > 0).map((p) => ({ method: p.method, amount: Number(p.amount) })),
    };
    try {
      const { data } = await api.post("/sales", payload);
      setLastSale(data);
      setPayOpen(false);
      setCart([]); setDiscount(0); setCustomerId("");
      setPayments([{ _k: crypto.randomUUID(), method: "Espèces", amount: "" }]);
      toast.success(`Vente ${data.number} enregistrée`);
      load();
      fetchProducts(search);
    } catch (e) {
      if (!e.response) {
        const ref = crypto.randomUUID();
        await queueAdd({ ref, payload: { ...payload, offline_ref: ref }, created_at: new Date().toISOString() });
        setPayOpen(false);
        setCart([]); setDiscount(0); setCustomerId("");
        setPayments([{ _k: crypto.randomUUID(), method: "Espèces", amount: "" }]);
        setPendingCount((c) => c + 1);
        toast.warning("Connexion perdue — vente enregistrée hors-ligne, synchronisation automatique au retour du réseau");
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4" data-testid="pos-page">
      {(offline || pendingCount > 0) && (
        <div data-testid="pos-offline-banner" className={`lg:col-span-5 flex items-center gap-2 flex-wrap rounded-xl border px-4 py-2 text-sm font-semibold ${
          offline ? "bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                  : "bg-blue-50 border-blue-300 text-blue-800 dark:bg-blue-950 dark:text-blue-200"}`}>
          <WifiOff className="h-4 w-4" />
          {offline ? "Mode hors-ligne : les ventes sont enregistrées localement et synchronisées automatiquement." : "Connexion rétablie."}
          {pendingCount > 0 && <span data-testid="pos-pending-count">{pendingCount} vente(s) en attente de synchronisation</span>}
          {!offline && pendingCount > 0 && (
            <button data-testid="pos-sync-now" onClick={syncQueue} className="ml-auto text-xs font-bold underline">Synchroniser maintenant</button>)}
        </div>
      )}
      <div className="lg:col-span-3 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input ref={searchRef} autoFocus data-testid="pos-search" value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={onScan}
              placeholder="Scanner code-barres ou rechercher (nom, réf, catégorie) + Entrée"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-input bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <select data-testid="pos-warehouse" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={`${selectCls} w-40 h-12`}>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 max-h-[calc(100vh-220px)] overflow-y-auto pr-1" data-testid="pos-products">
          {products.map((p) => (
            <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)}
              disabled={p.stock <= 0}
              className="text-left bg-card border rounded-xl p-3 min-h-[88px] hover:border-primary hover:shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed">
              {p.photo_path && <img data-testid={`pos-product-img-${p.id}`} src={fileUrl(p.photo_path)} alt="" className="h-16 w-full rounded-lg object-cover mb-2" />}
              <div className="text-sm font-semibold leading-tight line-clamp-2">{p.name}</div>
              <div className="mt-1.5 flex items-center justify-between gap-1">
                <span className="font-money font-bold text-primary text-sm">{fmtMoney(p.sale_price, cur)}</span>
                <span className={`text-[11px] font-semibold ${p.stock <= 0 ? "text-rose-600" : p.stock <= p.alert_threshold ? "text-amber-600" : "text-emerald-600"}`}>
                  {p.stock <= 0 ? "Rupture" : `${p.stock} ${p.unit}`}
                </span>
              </div>
              {p.stock > 0 && (p.units || []).length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-0.5">= {stockBreakdown(p, p.stock)}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-2 bg-card border rounded-xl flex flex-col max-h-[calc(100vh-140px)]" data-testid="pos-cart">
        <div className="p-3 border-b flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <span className="font-heading font-bold">Panier ({cart.length})</span>
        </div>
        <div className="p-3 border-b">
          <select data-testid="pos-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={selectCls}>
            <option value="">Client comptant</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.price_level !== "detail" ? ` (${c.price_level})` : ""}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Panier vide — scannez ou cliquez un produit</p>}
          {cart.map((l, i) => (
            <div key={`${l.product_id}-${l.unit || "base"}`} data-testid={`pos-cart-line-${i}`} className="border rounded-lg p-2 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold truncate">{l.name}</span>
                <button data-testid={`pos-remove-${i}`} onClick={() => removeLine(i)} className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 rounded p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center border rounded-lg">
                  <button data-testid={`pos-qty-minus-${i}`} onClick={() => setQty(i, l.qty - 1)} className="h-11 w-11 flex items-center justify-center hover:bg-muted rounded-l-lg"><Minus className="h-4 w-4" /></button>
                  <input data-testid={`pos-qty-${i}`} type="number" min="0.01" step="any" value={l.qty} onChange={(e) => setQty(i, Number(e.target.value))}
                    className="w-16 h-11 text-center font-money font-bold border-x bg-background focus:outline-none" />
                  <button data-testid={`pos-qty-plus-${i}`} onClick={() => setQty(i, l.qty + 1)} className="h-11 w-11 flex items-center justify-center hover:bg-muted rounded-r-lg"><Plus className="h-4 w-4" /></button>
                </div>
                {l.units.length > 0 && (
                  <select data-testid={`pos-unit-${i}`} value={l.unit} onChange={(e) => setUnit(i, e.target.value)} className="h-11 px-2 rounded-lg border bg-background text-sm">
                    <option value="">{l.base_unit}</option>
                    {l.units.map((u) => (
                      <option key={u.unit} value={u.unit}>
                        {unitOptionLabel(l.base_unit, u, Number(u.sale_price) > 0 ? u.sale_price : l.sale_price * u.factor, cur)}
                      </option>
                    ))}
                  </select>
                )}
                {hasPerm("sales.edit_price") ? (
                  <input data-testid={`pos-price-${i}`} type="number" value={l.unit_price} onChange={(e) => setPrice(i, Number(e.target.value))}
                    className="w-24 h-11 px-2 rounded-lg border bg-background font-money text-sm text-right focus:outline-none focus:ring-2 focus:ring-ring" />
                ) : (
                  <span className="font-money font-bold text-sm ml-auto">{fmtMoney(l.unit_price, cur)}</span>
                )}
              </div>
              <div className="text-right font-money font-bold text-sm">{fmtMoney(l.unit_price * l.qty - l.discount, cur)}</div>
            </div>
          ))}
        </div>
        <div className="border-t p-3 space-y-2">
          {hasPerm("sales.discount") && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Remise globale</span>
              <input data-testid="pos-discount" type="number" min="0" value={discount || ""} onChange={(e) => setDiscount(e.target.value)}
                className="w-28 h-9 px-2 rounded-lg border bg-background font-money text-sm text-right focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="font-heading font-bold text-lg">TOTAL</span>
            <span data-testid="pos-total" className="font-money font-bold text-xl text-primary">{fmtMoney(total, cur)}</span>
          </div>
          <button data-testid="pos-checkout" disabled={cart.length === 0} onClick={() => setPayOpen(true)}
            className={`${btnPrimary} w-full h-12 text-base`}>
            Encaisser
          </button>
        </div>
      </div>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Paiement">
        <div className="space-y-3">
          <div className="flex justify-between font-heading font-bold text-lg">
            <span>Total à payer</span><span className="font-money text-primary">{fmtMoney(total, cur)}</span>
          </div>
          {payments.map((p, i) => (
            <div key={p._k || i} className="flex gap-2">
              <select data-testid={`pay-method-${i}`} value={p.method}
                onChange={(e) => setPayments(payments.map((x, j) => j === i ? { ...x, method: e.target.value } : x))}
                className={`${selectCls} h-12 flex-1`}>
                {payMethods.map((m) => <option key={m}>{m}</option>)}
              </select>
              <input data-testid={`pay-amount-${i}`} type="number" min="0" placeholder="Montant" value={p.amount}
                onChange={(e) => setPayments(payments.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                className={`${inputCls} h-12 w-32 font-money text-right`} />
              {payments.length > 1 && (
                <button onClick={() => setPayments(payments.filter((_, j) => j !== i))} className="h-12 w-12 flex items-center justify-center text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950 rounded-lg"><X className="h-4 w-4" /></button>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="pay-add-method" onClick={() => setPayments([...payments, { _k: crypto.randomUUID(), method: "Wave", amount: "" }])} className={btnSecondary}>+ Paiement combiné</button>
            <button data-testid="pay-fill-cash" onClick={() => setPayments([{ _k: crypto.randomUUID(), method: "Espèces", amount: String(total) }])} className={btnSecondary}>Espèces exact</button>
          </div>
          <div className="flex justify-between text-sm font-semibold">
            <span>Encaissé : <span className="font-money">{fmtMoney(paidSum, cur)}</span></span>
            <span className={paidSum >= total ? "text-emerald-600" : "text-amber-600"}>
              {paidSum >= total ? `Monnaie : ${fmtMoney(paidSum - total, cur)}` : `Crédit : ${fmtMoney(total - paidSum, cur)}`}
            </span>
          </div>
          {paidSum < total && !customerId && (
            <p className="text-xs text-rose-600 font-medium">Un paiement partiel/crédit nécessite de sélectionner un client.</p>
          )}
          <button data-testid="pay-confirm" disabled={saving || (paidSum < total && !customerId)} onClick={submitSale} className={`${btnPrimary} w-full h-12`}>
            {saving ? "Enregistrement..." : "Valider la vente"}
          </button>
        </div>
      </Modal>

      <Modal open={!!lastSale} onClose={() => setLastSale(null)} title="Vente enregistrée">
        {lastSale && (
          <div className="space-y-4" data-testid="sale-success">
            <div className="text-center">
              <div className="font-heading font-bold text-xl">{lastSale.number}</div>
              <div className="font-money font-bold text-2xl text-primary mt-1">{fmtMoney(lastSale.total, cur)}</div>
              {lastSale.balance > 0 && <div className="text-amber-600 font-semibold text-sm mt-1">Reste à payer : {fmtMoney(lastSale.balance, cur)}</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="sale-print" onClick={() => openPdf(`/sales/${lastSale.id}/pdf`)} className={`${btnPrimary} flex-1`}>
                <Printer className="h-4 w-4" /> Facture A4
              </button>
              <button data-testid="sale-print-ticket" onClick={() => openPdf(`/sales/${lastSale.id}/ticket`)} className={`${btnSecondary} flex-1`}>
                <Printer className="h-4 w-4" /> Ticket 80mm
              </button>
              <button data-testid="sale-new" onClick={() => { setLastSale(null); searchRef.current?.focus(); }} className={`${btnSecondary} flex-1`}>Nouvelle vente</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
